import React, { useEffect, useState } from 'react';
import { shape, string } from 'prop-types';
import axios from 'axios';
import queryString from 'query-string';

import { getLoginURL } from '../../utils/redirect-utils';
import BasePage from '../BasePage/BasePage';
import { safeDecodeJwt } from '../../utils/jwt-utils';
import { PrsDefaultButton } from '../../styles-and-themes/styles/prsButtons';
import { getReturnUrlByDestination } from '../../../shared-with-server/destination-utils';
import { destinationInfo } from '../../destructured-front-end-config';
import ConfirmationErrorPanel from './Panels/ConfirmationErrorPanel/ConfirmationErrorPanel';
import BusyOverlay from '../../shared-panels/uam/BusyOverlay/BusyOverlay';

const ConfirmationCompletePage = ({ destPath, brand }) => {
  const [confirmationData, setConfirmationData] = useState(null);
  const [isLoginCheckComplete, setIsLoginCheckComplete] = useState(false);
  const { token: encodedToken } = queryString.parse(location.search);
  const decodedToken = safeDecodeJwt(encodedToken);

  useEffect(
    () => {
      if (decodedToken) {
        axios.get(
          `/iam/confirm?token=${encodedToken}`,
          {
            validateStatus: (status) => status === 200 || status === 409,
          },
        )
          .then((res) => {
            console.log('ConfirmationCompletePage, finished API call', { res, request: res.request });

            if (res.status === 409) {
              setConfirmationData({
                isError: true,
                destPath: res.data.destPath,
              });
            } else {
              setConfirmationData(res.data);
            }
          });
      } else {
        // it's weird, but we set the confirmation state to
        // true if we arrive at this page without a token
        setConfirmationData({});
      }
    },
    [],
  );

  useEffect(
    () => {
      if (!confirmationData || confirmationData.isError) { return; }

      axios.get(
        '/api/v2/fi/token',
        {
          validateStatus: (status) => status === 200 || status === 401 || status === 404,
        },
      ).then(
        (response) => {
          if (response.status === 200) {
            // User is already loggged in, send them to their destination
            window.location.href = getReturnUrlByDestination(
              destinationInfo,
              confirmationData.destPath.dest,
              confirmationData.destPath.path,
            ) || confirmationData.destPath.retURL;
          } else {
            setIsLoginCheckComplete(true);
          }
        },
      );
    },
    [confirmationData],
  );

  const getPostRegistrationUri = () => {
    if (!decodedToken) {
      return getLoginURL(destPath);
    }

    return decodedToken.oidcPostRegistrationLoginUri || getLoginURL(decodedToken);
  };

  const getShowBusyIndicator = () => {
    if (!confirmationData) return true;

    if (confirmationData.isError) return false;

    return !isLoginCheckComplete;
  };

  const showBusyIndicator = getShowBusyIndicator();

  return (
    <BasePage brand={brand}>
      { showBusyIndicator && (<BusyOverlay />) }
      {
        confirmationData?.isError === true && (
          <ConfirmationErrorPanel
            destPath={confirmationData.destPath}
          />
        )
      }
      <h2>Your account has been created.</h2>
      <div className="pad">
        <p className="description mainMessage">
          Thank you for registering with {brand.full}.
          {' '}Your account has been created.
        </p>
        {
          !!confirmationData?.destPath && !showBusyIndicator && (
            <>
              <br />
              <PrsDefaultButton
                title="Sign In"
                id="loginButton"
                name="loginButton"
                value="Sign In"
                onClick={() => { window.location.href = getPostRegistrationUri(); }}
                style={{
                  marginTop: '6px',
                  width: '100%',
                }}
                primary
                disabled={!confirmationData}
                ariaDisabled={!confirmationData}
              >
                Sign In
              </PrsDefaultButton>
            </>
          )
        }
      </div>
    </BasePage>
  );
};

ConfirmationCompletePage.propTypes = {
  destPath: shape({
    dest: string,
    path: string,
    retURL: string,
  }),
  brand: shape({
    short: string.isRequired,
    full: string.isRequired,
  }).isRequired,
};

ConfirmationCompletePage.defaultProps = {
  destPath: {
    retURL: null,
  },
};

export default ConfirmationCompletePage;
